/**
 * Leviathan Web - AR Aging Report Page
 * Quasar Prime LLC
 * Aggressive Tactical Industrial Design
 *
 * Accounts receivable aging report by customer
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  ArrowLeft,
  RefreshCw,
  Printer,
  AlertTriangle,
  Users,
  DollarSign,
  Clock,
} from 'lucide-react';
import { API_URL } from '@/config/api';

interface AgingCustomer {
  customer: {
    id: string;
    customerNumber: string;
    name: string;
    creditLimit: number | null;
  };
  current: number;
  over30: number;
  over60: number;
  over90: number;
  total: number;
  invoiceCount: number;
}

interface AgingReport {
  asOfDate: string;
  generatedAt: string;
  customers: AgingCustomer[];
  totals: {
    current: number;
    over30: number;
    over60: number;
    over90: number;
    total: number;
  };
  summary: {
    totalCustomers: number;
    totalOutstanding: number;
    averageAge: number;
  };
}

/**
 * Stat card component
 */
function StatCard({
  label,
  value,
  icon,
  status = 'neutral',
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
  status?: 'neutral' | 'warning' | 'critical';
}) {
  const statusColors = {
    neutral: 'border-panel-border text-amber',
    warning: 'border-warning-amber text-warning-amber',
    critical: 'border-error-red text-error-red',
  };

  return (
    <div
      className={`relative bg-panel-dark border ${statusColors[status].split(' ')[0]} p-4`}
    >
      <div className="absolute top-0 left-0 w-2 h-2 border-l border-t border-amber" />
      <div className="absolute top-0 right-0 w-2 h-2 border-r border-t border-amber" />
      <div className="absolute bottom-0 left-0 w-2 h-2 border-l border-b border-amber" />
      <div className="absolute bottom-0 right-0 w-2 h-2 border-r border-b border-amber" />

      <div className="flex items-center space-x-2 mb-2">
        <span className={`${statusColors[status].split(' ')[1]}`}>{icon}</span>
        <span className="text-text-tertiary text-xs uppercase tracking-widest font-mono">
          {label}
        </span>
      </div>
      <div
        className={`text-xl font-mono font-bold ${statusColors[status].split(' ')[1]}`}
        style={{ textShadow: '0 0 10px currentColor' }}
      >
        {value}
      </div>
    </div>
  );
}

/**
 * AR Aging Report Page
 */
const ARAgingReport = () => {
  const { accessToken } = useAuth();
  const [report, setReport] = useState<AgingReport | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [asOfDate, setAsOfDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    fetchReport();
  }, [asOfDate]);

  const fetchReport = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/reports/ar-aging?asOfDate=${asOfDate}`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );
      if (response.ok) {
        const data = await response.json();
        setReport(data.report);
      }
    } catch (error) {
      console.error('Failed to fetch report:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const getAgingColor = (amount: number, bucket: string) => {
    if (amount === 0) return 'text-text-tertiary';
    if (bucket === 'over90') return 'text-error-red';
    if (bucket === 'over60') return 'text-warning-amber';
    if (bucket === 'over30') return 'text-amber';
    return 'text-text-primary';
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to="/reports"
            className="p-2 text-text-tertiary hover:text-amber transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-text-primary font-mono uppercase tracking-wider">
              AR Aging Report
            </h1>
            <p className="text-sm text-text-secondary font-mono mt-1">
              Accounts receivable aging by customer
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => window.print()}
            className="flex items-center space-x-2 px-4 py-2 border border-panel-border text-text-secondary hover:text-amber hover:border-amber transition-colors"
          >
            <Printer className="w-4 h-4" />
            <span className="font-mono text-sm">Print</span>
          </button>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-panel-dark border border-panel-border p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
              As of Date
            </label>
            <input
              type="date"
              value={asOfDate}
              onChange={(e) => setAsOfDate(e.target.value)}
              className="bg-background border border-panel-border focus:border-amber px-4 py-2 text-text-primary font-mono focus:outline-none"
            />
          </div>
          <button
            onClick={fetchReport}
            disabled={isLoading}
            className="flex items-center space-x-2 bg-amber text-background px-4 py-2 font-mono text-sm uppercase tracking-wider hover:bg-amber/80 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      {report && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            label="Total Outstanding"
            value={formatCurrency(report.summary.totalOutstanding)}
            icon={<DollarSign className="w-5 h-5" />}
          />
          <StatCard
            label="Customers"
            value={report.summary.totalCustomers.toString()}
            icon={<Users className="w-5 h-5" />}
          />
          <StatCard
            label="Average Age"
            value={`${report.summary.averageAge.toFixed(0)} days`}
            icon={<Clock className="w-5 h-5" />}
          />
          <StatCard
            label="Over 90 Days"
            value={formatCurrency(report.totals.over90)}
            icon={<AlertTriangle className="w-5 h-5" />}
            status={report.totals.over90 > 0 ? 'critical' : 'neutral'}
          />
        </div>
      )}

      {/* Aging Table */}
      <div className="relative">
        <div className="absolute top-0 left-0 w-4 h-4 border-l-2 border-t-2 border-amber" />
        <div className="absolute top-0 right-0 w-4 h-4 border-r-2 border-t-2 border-amber" />
        <div className="absolute bottom-0 left-0 w-4 h-4 border-l-2 border-b-2 border-amber" />
        <div className="absolute bottom-0 right-0 w-4 h-4 border-r-2 border-b-2 border-amber" />

        <div className="bg-panel-dark border border-panel-border overflow-x-auto">
          {isLoading ? (
            <div className="text-center py-12 text-text-tertiary font-mono">
              <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
              Loading report...
            </div>
          ) : !report || report.customers.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-4xl mb-2">✅</div>
              <div className="text-text-tertiary font-mono">No outstanding receivables</div>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-background border-b border-panel-border">
                <tr>
                  <th className="text-left px-4 py-3 text-text-secondary text-[10px] uppercase tracking-widest font-mono">
                    Customer
                  </th>
                  <th className="text-right px-4 py-3 text-text-secondary text-[10px] uppercase tracking-widest font-mono">
                    Current
                  </th>
                  <th className="text-right px-4 py-3 text-text-secondary text-[10px] uppercase tracking-widest font-mono">
                    31-60
                  </th>
                  <th className="text-right px-4 py-3 text-text-secondary text-[10px] uppercase tracking-widest font-mono">
                    61-90
                  </th>
                  <th className="text-right px-4 py-3 text-text-secondary text-[10px] uppercase tracking-widest font-mono">
                    90+
                  </th>
                  <th className="text-right px-4 py-3 text-text-secondary text-[10px] uppercase tracking-widest font-mono">
                    Total
                  </th>
                  <th className="text-right px-4 py-3 text-text-secondary text-[10px] uppercase tracking-widest font-mono">
                    Credit Limit
                  </th>
                </tr>
              </thead>
              <tbody>
                {report.customers.map((row) => (
                  <tr
                    key={row.customer.id}
                    className="border-b border-panel-border/50 hover:bg-amber/5"
                  >
                    <td className="px-4 py-3">
                      <Link
                        to={`/customers/${row.customer.id}`}
                        className="text-amber hover:text-amber/80 font-mono font-semibold"
                      >
                        {row.customer.name}
                      </Link>
                      <div className="text-text-tertiary text-xs font-mono">
                        {row.customer.customerNumber} &bull; {row.invoiceCount} invoice
                        {row.invoiceCount !== 1 ? 's' : ''}
                      </div>
                    </td>
                    <td
                      className={`px-4 py-3 text-right font-mono ${getAgingColor(row.current, 'current')}`}
                    >
                      {row.current > 0 ? formatCurrency(row.current) : '-'}
                    </td>
                    <td
                      className={`px-4 py-3 text-right font-mono ${getAgingColor(row.over30, 'over30')}`}
                    >
                      {row.over30 > 0 ? formatCurrency(row.over30) : '-'}
                    </td>
                    <td
                      className={`px-4 py-3 text-right font-mono ${getAgingColor(row.over60, 'over60')}`}
                    >
                      {row.over60 > 0 ? formatCurrency(row.over60) : '-'}
                    </td>
                    <td
                      className={`px-4 py-3 text-right font-mono ${getAgingColor(row.over90, 'over90')}`}
                    >
                      {row.over90 > 0 ? formatCurrency(row.over90) : '-'}
                    </td>
                    <td className="px-4 py-3 text-right font-mono font-bold text-amber">
                      {formatCurrency(row.total)}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-text-secondary">
                      {row.customer.creditLimit
                        ? formatCurrency(row.customer.creditLimit)
                        : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-background border-t-2 border-amber font-bold">
                <tr>
                  <td className="px-4 py-3 text-text-primary font-mono uppercase">Totals</td>
                  <td className="px-4 py-3 text-right font-mono text-text-primary">
                    {formatCurrency(report.totals.current)}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-amber">
                    {formatCurrency(report.totals.over30)}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-warning-amber">
                    {formatCurrency(report.totals.over60)}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-error-red">
                    {formatCurrency(report.totals.over90)}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-amber text-lg">
                    {formatCurrency(report.totals.total)}
                  </td>
                  <td className="px-4 py-3"></td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      </div>

      {/* Report Info */}
      {report && (
        <div className="text-center text-text-tertiary font-mono text-xs">
          Report generated: {new Date(report.generatedAt).toLocaleString()} | As of:{' '}
          {new Date(report.asOfDate).toLocaleDateString()}
        </div>
      )}
    </div>
  );
};

export default ARAgingReport;
